import axios from 'axios';
import type { Position, RouteData } from '../types.js';
import type { RouteProfile } from '../components/profileSelector/RouteProfileSelector.js';
import type { SessionFunctions } from '../services/storage/RouteStorageManager.js';
import { handleRoutingError } from '../services/errorHandler';
import { ErrorCodes } from '../types/errors';
import { aggregateElevationData } from './elevationAggregation';
import { detectClimbs, DEFAULT_CLIMB_CONFIG } from './climbDetection';

// Enhanced timeout configuration
export interface RoutingTimeoutConfig {
  timeout?: number;
  cancelToken?: any;
}

// Make timeout configurable from external sources
let globalRoutingTimeout = 30000; // Default 30 seconds

export const setGlobalRoutingTimeout = (timeout: number): void => {
  globalRoutingTimeout = timeout;
};


export type RouteFormat = 'json' | 'geojson' | 'gpx';

// Cache für Routenberechnungen, um doppelte API-Aufrufe zu vermeiden
const routeCache = new Map<string, Promise<RouteData | null>>();
const CACHE_TTL = 5 * 60 * 1000; // 5 Minuten

// Helper-Funktion um Cache-Keys zu erstellen
const createCacheKey = (positions: Position[], profile: RouteProfile, format: RouteFormat): string => {
  const positionsString = positions.map(p => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`).join('|');
  return `${positionsString}-${profile}-${format}`;
};

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Erdradius in Metern
  const φ1 = lat1 * Math.PI/180;
  const φ2 = lat2 * Math.PI/180;
  const Δφ = (lat2-lat1) * Math.PI/180;
  const Δλ = (lon2-lon1) * Math.PI/180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
           Math.cos(φ1) * Math.cos(φ2) *
           Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  return R * c; // Distanz in Metern
};

const calculateSteepness = (elevation1: number, elevation2: number, distance: number): number => {
  if (distance === 0) return 0;
  return ((elevation2 - elevation1) / distance) * 100;
};

// Surface categorization by quality/type
export const SURFACE_CATEGORIES = {
  PAVED: 'paved',
  GRAVEL: 'gravel',
  UNPAVED: 'unpaved',
  COBBLESTONE: 'cobblestone',
  GRASS: 'grass',
  ROUGH: 'rough',
  UNKNOWN: 'unknown'
} as const;

export type SurfaceCategory = typeof SURFACE_CATEGORIES[keyof typeof SURFACE_CATEGORIES];

// Surface definitions by category
const SURFACE_DEFINITIONS = {
  // Paved surfaces - smooth, sealed surfaces
  [SURFACE_CATEGORIES.PAVED]: new Set([
    'paved', 'rubber', 'acrylic', 'tartan'
  ]),
  
  // Gravel surfaces - loose aggregate materials
  [SURFACE_CATEGORIES.GRAVEL]: new Set([
    'gravel'
  ]),
  
  // Unpaved surfaces - natural earth surfaces
  [SURFACE_CATEGORIES.UNPAVED]: new Set([
    'unpaved'
  ]),
  
  // Cobblestone surfaces - stone block surfaces
  [SURFACE_CATEGORIES.COBBLESTONE]: new Set([
    'cobblestone'
  ]),
  
  // Grass surfaces - natural vegetation surfaces
  [SURFACE_CATEGORIES.GRASS]: new Set([
    'grass'
  ]),
  
  // Rough surfaces - challenging or technical surfaces
  [SURFACE_CATEGORIES.ROUGH]: new Set([
    'metal', 'wood', 'rock', 'sand', 'mud', 'snow', 'ice', 'salt', 'clay',
    'stepping_stones', 'fibre_reinforced_polymer_grate', 'shells', 'woodchips',
    'carpet', 'plastic'
  ])
};

// Function to categorize a surface type
export const categorizeSurface = (surface: string): SurfaceCategory => {
  if (!surface || typeof surface !== 'string') {
    return SURFACE_CATEGORIES.UNKNOWN;
  }
  
  const normalizedSurface = surface.toLowerCase().trim();
  
  // Check each category
  for (const [category, surfaces] of Object.entries(SURFACE_DEFINITIONS)) {
    if (surfaces.has(normalizedSurface)) {
      return category as SurfaceCategory;
    }
  }
  
  return SURFACE_CATEGORIES.UNKNOWN;
};

// Function to get human-readable category name
export const getCategoryDisplayName = (category: SurfaceCategory): string => {
  const displayNames = {
    [SURFACE_CATEGORIES.PAVED]: 'Paved Surfaces',
    [SURFACE_CATEGORIES.GRAVEL]: 'Gravel Surfaces',
    [SURFACE_CATEGORIES.UNPAVED]: 'Unpaved Surfaces',
    [SURFACE_CATEGORIES.COBBLESTONE]: 'Cobblestone Surfaces',
    [SURFACE_CATEGORIES.GRASS]: 'Grass Surfaces',
    [SURFACE_CATEGORIES.ROUGH]: 'Rough Surfaces',
    [SURFACE_CATEGORIES.UNKNOWN]: 'Unknown Surfaces'
  };
  
  return displayNames[category] || 'Unknown Surfaces';
};

// Function to get category description
export const getCategoryDescription = (category: SurfaceCategory): string => {
  const descriptions = {
    [SURFACE_CATEGORIES.PAVED]: 'Smooth, sealed surfaces like asphalt and concrete',
    [SURFACE_CATEGORIES.GRAVEL]: 'Loose aggregate surfaces, suitable for most bikes',
    [SURFACE_CATEGORIES.UNPAVED]: 'Natural earth surfaces, may vary in firmness',
    [SURFACE_CATEGORIES.COBBLESTONE]: 'Stone block surfaces, bumpy but stable',
    [SURFACE_CATEGORIES.GRASS]: 'Natural vegetation surfaces, soft and variable',
    [SURFACE_CATEGORIES.ROUGH]: 'Challenging surfaces requiring careful navigation',
    [SURFACE_CATEGORIES.UNKNOWN]: 'Surface type not identified'
  };
  
  return descriptions[category] || 'Surface type not identified';
};

// Function to get category color for UI
export const getCategoryColor = (category: SurfaceCategory): string => {
  const colors = {
    [SURFACE_CATEGORIES.PAVED]: '#D3D3D3',        // Light Grey - smooth
    [SURFACE_CATEGORIES.GRAVEL]: '#8B4513',       // Brown - moderate
    [SURFACE_CATEGORIES.UNPAVED]: '#696969',      // Dark Grey - natural
    [SURFACE_CATEGORIES.COBBLESTONE]: '#654321',  // Dark Brown - historic
    [SURFACE_CATEGORIES.GRASS]: '#4CAF50',        // Green - natural
    [SURFACE_CATEGORIES.ROUGH]: '#F44336',        // Red - challenging
    [SURFACE_CATEGORIES.UNKNOWN]: '#000000'       // Black - unknown
  };
  
  return colors[category] || '#000000';
};

// Helper function to parse BRouter messages into structured segments data
const parseMessagesToSegments = (messages: any[]): any[] => {
  if (!messages || messages.length < 2) {
    return [];
  }
  
  // First element contains column headers
  const headers = messages[0];
  if (!Array.isArray(headers)) {
    return [];
  }
  
  // Surface aggregation mapping - consolidate similar surfaces into main categories
  const surfaceAggregationMap: Record<string, string> = {
    // Paved surfaces
    'asphalt': 'paved',
    'paving_stones': 'paved',
    'paving_stones:lanes': 'paved',
    'concrete': 'paved',
    'concrete:lanes': 'paved',
    'concrete:plates': 'paved',
    'paved': 'paved',
    'sett': 'paved',
    'bricks': 'paved',
    'tiles': 'paved',
    
    // Gravel surfaces
    'gravel': 'gravel',
    'fine_gravel': 'gravel',
    'chipseal': 'gravel',
    'compacted': 'gravel',
    
    // Unpaved surfaces
    'unpaved': 'unpaved',
    'dirt': 'unpaved',
    'earth': 'unpaved',
    'ground': 'unpaved',
    
    // Cobblestone surfaces
    'cobblestone': 'cobblestone',
    'cobblestone:flattened': 'cobblestone',
    'unhewn_cobblestone': 'cobblestone',
    'pebblestone': 'cobblestone',
    
    // Grass surfaces
    'grass': 'grass',
    'grass_paver': 'grass',
    'artificial_turf': 'grass',
    
    // Special surfaces
    'wood': 'gravel',
    'metal': 'paved',
    'metal_grid': 'paved',
    'rock': 'rock',
    'sand': 'sand',
    'mud': 'mud',
    'snow': 'snow',
    'ice': 'ice',
    
    // Unknown/missing surface data
    'unknown': 'unknown',
    
    // Keep other surfaces as-is or add more mappings as needed
  };
  
  // Function to aggregate surface types
  const aggregateSurface = (originalSurface: string): string => {
    if (!originalSurface || typeof originalSurface !== 'string') {
      return originalSurface;
    }
    
    const normalized = originalSurface.toLowerCase().trim();
    return surfaceAggregationMap[normalized] || originalSurface;
  };
  
  // Function to derive surface from highway and tracktype tags when surface is missing
  const deriveSurfaceFromTags = (segment: any): string | null => {
    const highway = segment.highway?.toLowerCase();
    const tracktype = segment.tracktype?.toLowerCase();
    
    // Highway-based surface derivation
    if (highway) {
      switch (highway) {
        case 'motorway':
        case 'motorway_link':
        case 'trunk':
        case 'trunk_link':
        case 'primary':
        case 'primary_link':
        case 'secondary':
        case 'secondary_link':
        case 'tertiary':
        case 'tertiary_link':
        case 'unclassified':
        case 'residential':
        case 'service':
          return 'paved'; // Most roads are paved
          
        case 'track':
          // For tracks, use tracktype if available, otherwise assume unpaved
          if (tracktype) {
            switch (tracktype) {
              case 'grade1': return 'paved'; // Solid surface, usually paved
              case 'grade2': return 'gravel'; // Mostly solid, usually gravel
              case 'grade3': return 'unpaved'; // Mixed surface, earth/gravel mix
              case 'grade4':
              case 'grade5': return 'unpaved'; // Soft/very soft surface
              default: return 'unpaved';
            }
          }
          return 'unpaved';
          
        case 'path':
        case 'footway':
        case 'cycleway':
          return 'unpaved'; // Usually unpaved unless specified
          
        case 'bridleway':
          return 'unpaved'; // Horse tracks, usually unpaved
          
        case 'steps':
          return 'paved'; // Steps are usually paved/concrete
          
        default:
          return null; // Unknown highway type
      }
    }
    
    // If no highway tag, try tracktype alone
    if (tracktype) {
      switch (tracktype) {
        case 'grade1': return 'paved';
        case 'grade2': return 'gravel';
        case 'grade3':
        case 'grade4':
        case 'grade5': return 'unpaved';
        default: return null;
      }
    }
    
    return null; // No useful tags found
  };
  
  // Helper function to parse WayTags string into individual properties
  const parseWayTags = (wayTagsString: string): Record<string, string> => {
    const tags: Record<string, string> = {};
    if (!wayTagsString || typeof wayTagsString !== 'string') {
      return tags;
    }
    
    // Split by space and parse key=value pairs
    const tagPairs = wayTagsString.split(' ');
    
    tagPairs.forEach(pair => {
      const equalIndex = pair.indexOf('=');
      if (equalIndex > 0 && equalIndex < pair.length - 1) {
        const key = pair.substring(0, equalIndex);
        const value = pair.substring(equalIndex + 1);
        tags[key] = value;
      }
    });
    
    return tags;
  };
  
  // Parse remaining elements as data rows
  const segments = [];
  
  for (let i = 1; i < messages.length; i++) {
    const row = messages[i];
    if (!Array.isArray(row)) continue;
    
    // Create object mapping headers to values
    const segment: any = {};
    headers.forEach((header: string, index: number) => {
      segment[header] = row[index] !== undefined ? row[index] : null;
    });
    
    // Parse WayTags if it exists
    if (segment.WayTags && typeof segment.WayTags === 'string') {
      const parsedTags = parseWayTags(segment.WayTags);
      // Add parsed tags directly to the segment object
      Object.keys(parsedTags).forEach(key => {
        segment[key] = parsedTags[key];
      });
    }
    
    // Apply surface aggregation if surface exists, or try to derive it from other tags
    if (segment.surface) {
      const originalSurface = segment.surface;
      segment.surface = aggregateSurface(originalSurface);
      
      // Keep track of original surface for debugging if needed
      if (originalSurface !== segment.surface) {
        segment.originalSurface = originalSurface;
      }
      
      segment.surfaceCategory = categorizeSurface(segment.surface);
      segment.categoryDisplayName = getCategoryDisplayName(segment.surfaceCategory);
    } else {
      // Try to derive surface from highway/tracktype tags
      const derivedSurface = deriveSurfaceFromTags(segment);
      if (derivedSurface) {
        segment.surface = derivedSurface;
        segment.surfaceCategory = categorizeSurface(segment.surface);
        segment.categoryDisplayName = getCategoryDisplayName(segment.surfaceCategory);
        segment.surfaceDerived = true; // Mark as derived for debugging
      } else {
        // No surface information available, classify as unknown
        segment.surface = 'unknown';
        segment.surfaceCategory = SURFACE_CATEGORIES.UNKNOWN;
        segment.categoryDisplayName = getCategoryDisplayName(SURFACE_CATEGORIES.UNKNOWN);
        segment.surfaceDerived = false;
      }
    }
    
    segments.push(segment);
  }
  
  // Count surface data sources after processing all segments
  let _segmentsWithOriginalSurface = 0;
  let _segmentsWithDerivedSurface = 0;
  let _segmentsWithUnknownSurface = 0;
  
  segments.forEach(segment => {
    if (segment.surface && segment.surface !== 'unknown') {
      if (segment.surfaceDerived === true) {
        _segmentsWithDerivedSurface++;
      } else if (segment.surfaceDerived === false) {
        _segmentsWithUnknownSurface++;
      } else {
        _segmentsWithOriginalSurface++;
      }
    } else {
      _segmentsWithUnknownSurface++;
    }
  });

  return segments;
};

/**
 * Parse waypoint error details from the routing API
 */
function parseWaypointError(details: string): { type: string; waypoint: string; position?: number } | null {
  if (!details) return null;
  
  if (details.includes('from-position not mapped')) {
    return { type: 'not_mapped', waypoint: 'start', position: 0 };
  } else if (details.includes('to-position not mapped')) {
    return { type: 'not_mapped', waypoint: 'end', position: -1 };
  } else {
    // Generic handler for all via waypoints
    const viaMatch = details.match(/via(\d+)-position not mapped/);
    if (viaMatch) {
      const viaNum = parseInt(viaMatch[1], 10);
      return { type: 'not_mapped', waypoint: `via${viaNum}`, position: viaNum };
    }
  }
  
  return null;
}

export const calculateRoute = async (
  positions: Position[], 
  profile: RouteProfile = 'trekking',
  format: RouteFormat = 'geojson',
  saveFullDataForRouteId?: string, // Optional: save full route data for this route ID
  sessionFunctions?: SessionFunctions, // Optional: session functions for API calls
  bypassCache?: boolean, // Optional: bypass all caching (for loading saved routes)
  timeoutConfig?: RoutingTimeoutConfig // Optional: timeout and cancellation configuration
): Promise<RouteData | null> => {
  if (positions.length < 2) return null;

  // Erstelle Cache-Key und prüfe Cache (skip if bypassCache is true)
  const cacheKey = createCacheKey(positions, profile, format);

  
  // Prüfe, ob bereits eine identische Anfrage läuft (skip if bypassCache is true)
  if (!bypassCache && routeCache.has(cacheKey)) {
    return routeCache.get(cacheKey)!;
  }

  // Erstelle Promise für API-Aufruf und speichere es im Cache
  const routePromise = (async (): Promise<RouteData | null> => {
    try {
      const profileParams = {
        trekking: 'trekking',     // Standard-Fahrradprofil
        road: 'mk-road-bike',     // Rennrad (bevorzugt Straßen)
        gravel: 'gravel',     // Gravel (ausgewogenes Profil)
        mtb: 'mtb'          // MTB (inkl. Trails und Wege)
      };
      
      // Erstelle die Koordinaten-String im Format lon1,lat1|lon2,lat2|...
      const coordinatesString = positions
        .map(pos => `${pos.lng},${pos.lat}`)
        .join('|');

      // Use geocoding proxy for all API calls (both development and production)
      const baseUrl = import.meta.env.DEV 
        ? 'http://localhost:3333/api'  // Development: direct to geocoding proxy
        : '/api';                      // Production: through nginx to geocoding proxy 
      
      // Decide between GET and POST based on coordinate string length
      // URLs over ~2000 characters can cause "431 Request Header Fields Too Large" errors
      const usePost = coordinatesString.length > 1500; // Safe threshold
      
      // Add cache-busting parameter if bypassing cache
      const cacheBustParam = bypassCache ? `&_cb=${Date.now()}` : '';
      
  // Build axios request configuration with timeout and cancellation support
  const axiosConfig: any = {
    ...(format === 'gpx' && {
      headers: { 'Accept': 'application/gpx+xml' },
      responseType: 'text'
    }),
    timeout: timeoutConfig?.timeout || globalRoutingTimeout,
    ...(timeoutConfig?.cancelToken && { cancelToken: timeoutConfig.cancelToken })
  };

  let response: any;
  
  if (usePost) {
    
    const postData: any = {
      lonlats: coordinatesString,
      profile: profileParams[profile],
      alternativeidx: 0,
      trackAttrs: 'surface',
      addWaypoints: 1,
      instructions: 1,
      nogpx: 0,
      format: format
    };
    
    // Add cache-busting parameter for POST requests
    if (bypassCache) {
      postData._cb = Date.now();
    }
    
    response = await axios.post(`${baseUrl}/brouter`, postData, axiosConfig);
  } else {
    
    // Build URL manually to avoid URL encoding of coordinates
    const url = `${baseUrl}/brouter?lonlats=${coordinatesString}&profile=${profileParams[profile]}&alternativeidx=0&trackAttrs=surface&addWaypoints=1&instructions=1&nogpx=0&format=${format}${cacheBustParam}`;

    response = await axios.get(url, axiosConfig);
  }      // Wenn GPX angefordert wurde, geben wir die Rohdaten zurück
      if (format === 'gpx') {
        return response.data as unknown as RouteData;
      }
      // Für GeoJSON und JSON verarbeiten wir die Daten wie gewohnt
      if (response.data && response.data.features && response.data.features[0]) {
        const routeFeature = response.data.features[0];
        
        // First calculate all distances to use for both coordinates and elevation data
        const distanceData: number[] = [];
        let accumulatedDistance = 0;
        
        routeFeature.geometry.coordinates.forEach((coord: number[], index: number, array: number[][]) => {
          const segmentDistance = index > 0 
            ? calculateDistance(array[index-1][1], array[index-1][0], coord[1], coord[0]) 
            : 0;
          accumulatedDistance += segmentDistance / 1000; // Convert to km
          distanceData.push(accumulatedDistance);
        });
        
        // Create coordinates array WITH accumulated distance
        const coordinates = routeFeature.geometry.coordinates.map((coord: number[], index: number) => ({
          lng: coord[0],
          lat: coord[1],
          distance: distanceData[index] // Add accumulated distance to each coordinate
        }));

        // Berechne Höhendaten und Steigung
        const elevationData = routeFeature.geometry.coordinates.map((coord: number[], index: number, array: number[][]) => {
          const elevation = coord[2] || 0;
          const segmentDistance = index > 0 
            ? calculateDistance(array[index-1][1], array[index-1][0], coord[1], coord[0]) 
            : 0;

          return {
            distance: distanceData[index], // Use pre-calculated accumulated distance
            elevation,
            steepness: calculateSteepness(
              index > 0 ? array[index-1][2] || 0 : elevation,
              elevation,
              segmentDistance
            )
          };
        });

        // Detect climbs on FULL elevation data before aggregation for accuracy
        const climbs = detectClimbs(elevationData, DEFAULT_CLIMB_CONFIG);
        
        // Aggregate each climb's elevation profile to reduce data
        const aggregatedClimbs = climbs.map(climb => ({
          ...climb,
          elevationProfile: aggregateElevationData(climb.elevationProfile, 150)
        }));

        // Aggregate elevation data to 150 points for optimal chart performance
        // This prevents lag by reducing the amount of data the chart needs to process
        const aggregatedElevationData = aggregateElevationData(elevationData, 150);

        // Extrahiere Eigenschaften aus den Features
        const properties = routeFeature.properties || {};
        
        // Parse messages into structured segments
        const rawSegments = parseMessagesToSegments(properties.messages);
        
        // Combine consecutive segments with the same surface type using advanced logic
        const segments = combineSegmentsAdvanced(rawSegments);
        
        const routeData = {
          coordinates,
          distance: properties['track-length'], // Konvertiere zu Kilometern
          duration: properties['total-time'],
          ascend: properties['filtered ascend'],
          elevationData: aggregatedElevationData, // Use aggregated data for performance
          fullElevationData: elevationData, // Store full resolution data for zoom re-aggregation
          segments, // Aggregated segments for surface analysis
          rawSegments, // Original segments for duration calculation
          climbs: aggregatedClimbs // Pre-calculated climbs from full resolution data
        };

        // Save full route data if route ID is provided
        if (saveFullDataForRouteId) {

          // Import dynamically to avoid circular dependencies
          import('./routeFullDataUtils.js').then(({ saveFullRouteData }) => {
            saveFullRouteData(saveFullDataForRouteId, routeData, profile, response.data, sessionFunctions).catch(error => {
              console.warn('Failed to save full route data:', error);
            });
          });
        }
        
        return routeData;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      // Enhanced error handling with proper categorization
      let errorCode;
      let enhancedError = error;

      // Check if this is a cancellation error - if so, don't log it as an error
      if (axios.isCancel(error)) {
        console.warn('🚫 CALCULATE ROUTE: Route calculation was cancelled in calculateRoute function');
        console.warn('🚫 CALCULATE ROUTE: Cancel details:', {
          message: error.message,
          name: (error as any).name,
          stack: (error as any).stack?.substring(0, 200) + '...',
          isAxiosCancel: axios.isCancel(error),
          errorType: typeof error
        });
        throw error; // Re-throw the cancellation to be handled by the calling code
      }

      // Categorize common routing errors
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const responseData = error.response?.data;
        
        if (status === 404) {
          errorCode = ErrorCodes.ROUTE_NO_PATH;
          enhancedError = new Error('No route could be found between these points. Try selecting locations with road access.');
        } else if (status === 400) {
          // Check for specific waypoint mapping errors (this is the expected status for these errors)
          if (responseData?.details) {
            const details = responseData.details;
            if (details.includes('thread-priority-watchdog') || details.includes('operation killed')) {
              errorCode = ErrorCodes.NETWORK_TIMEOUT;
              enhancedError = new Error('Route calculation exceeded the 60-second timeout limit. This usually happens when waypoints are too far from roads or paths. Please check that all route points are placed near accessible routes.');
            } else if (details.includes('from-position not mapped')) {
              errorCode = ErrorCodes.ROUTE_START_NOT_MAPPED;
              enhancedError = new Error('Your starting location is not accessible by road. Please select a point closer to a road or path.');
            } else if (details.includes('to-position not mapped')) {
              errorCode = ErrorCodes.ROUTE_END_NOT_MAPPED;
              enhancedError = new Error('Your destination is not accessible by road. Please select a point closer to a road or path.');
            } else if (details.includes('-position not mapped')) {
              // Generic via waypoint handler for all waypoints (via1, via2, via3, etc.)
              const viaMatch = details.match(/via(\d+)-position not mapped/);
              if (viaMatch) {
                const waypointNum = parseInt(viaMatch[1], 10) + 1; // Convert to human-readable (via1 = waypoint 2)
                errorCode = ErrorCodes.ROUTE_WAYPOINT_NOT_MAPPED;
                enhancedError = new Error(`Your waypoint #${waypointNum} is not accessible by road. Please move it closer to a road or path.`);
              } else {
                errorCode = ErrorCodes.ROUTE_INVALID_WAYPOINTS;
                enhancedError = new Error('One of your waypoints is not accessible by road. Please check all points are on or near roads.');
              }
            } else if (details.includes('coordinate') || details.includes('invalid')) {
              errorCode = ErrorCodes.ROUTE_INVALID_WAYPOINTS;
              enhancedError = new Error('One or more waypoints have invalid coordinates. Please check your selected points.');
            } else {
              errorCode = ErrorCodes.ROUTE_INVALID_WAYPOINTS;
              enhancedError = new Error('Invalid waypoints selected. Please choose accessible road locations.');
            }
          } else {
            errorCode = ErrorCodes.ROUTE_INVALID_WAYPOINTS;
            enhancedError = new Error('Invalid waypoints selected. Please choose accessible road locations.');
          }
        } else if (status && status >= 500) {
          // Check if 500 error is actually a timeout from the server
          const errorField = responseData?.error || '';
          const messageField = responseData?.message || '';
          const detailsField = responseData?.details || '';
          const combinedText = `${errorField} ${messageField} ${detailsField}`.toLowerCase();
          
          console.log('🔍 500 Error Details:', { 
            status, 
            responseData,
            errorField,
            messageField,
            detailsField,
            combinedText,
            hasTimeout: combinedText.includes('timeout')
          });
          
          if (combinedText.includes('timeout') || combinedText.includes('operation killed') || combinedText.includes('thread-priority-watchdog')) {
            errorCode = ErrorCodes.NETWORK_TIMEOUT;
            enhancedError = new Error('Route calculation exceeded the 60-second timeout limit. This usually happens when waypoints are too far from roads or paths. Please check that all route points are placed near accessible routes.');
          } else {
            errorCode = ErrorCodes.ROUTE_CALCULATION_FAILED;
            enhancedError = new Error('Route calculation service is temporarily unavailable. Please try again later.');
          }
        } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          errorCode = ErrorCodes.NETWORK_TIMEOUT;
          enhancedError = new Error('Route calculation exceeded the 60-second timeout limit. This usually happens when waypoints are too far from roads or paths. Please check that all route points are placed near accessible routes.');
        } else if (error.code === 'ECONNREFUSED' || error.code === 'ENETUNREACH' || error.code === 'ENOTFOUND' || 
                   error.message?.includes('ECONNREFUSED') || error.message?.includes('fetch')) {
          errorCode = ErrorCodes.NETWORK_UNAVAILABLE;
          enhancedError = new Error('BRouter routing service is not available. Please check if the routing service is running.');
        }
      } else if (error instanceof Error) {
        if (error.message.includes('Invalid response format')) {
          errorCode = ErrorCodes.ROUTE_CALCULATION_FAILED;
          enhancedError = new Error('Route calculation failed due to invalid data. Please try different waypoints.');
        }
      }

      // Log the error with context using our error handling system
      await handleRoutingError(enhancedError, 'calculate_route', {
        positions: positions.length,
        profile,
        format,
        coordinates: positions.map(p => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`),
        errorCode,
        originalError: error instanceof Error ? error.message : String(error),
        httpStatus: axios.isAxiosError(error) ? error.response?.status : undefined,
        apiDetails: axios.isAxiosError(error) ? error.response?.data?.details : undefined,
        waypointAnalysis: axios.isAxiosError(error) && error.response?.data?.details ? 
          parseWaypointError(error.response.data.details) : undefined
      });

      return null;
    }
  })();

  // Speichere Promise im Cache (skip if bypassCache is true)
  if (!bypassCache) {
    routeCache.set(cacheKey, routePromise);
    
    // Lösche aus Cache nach Ablauf (oder bei Fehler)
    setTimeout(() => {
      routeCache.delete(cacheKey);
    }, CACHE_TTL);
  }

  return routePromise;
};

export const downloadRouteAsGPX = async (
  positions: Position[], 
  profile: RouteProfile = 'trekking', 
  routeName?: string,
  onLoadingStart?: () => void,
  onLoadingEnd?: () => void,
  sessionFunctions?: SessionFunctions
) => {
  try {
    
    // Notify that loading has started
    if (onLoadingStart) {
      onLoadingStart();
    }
    
    const gpxData = await calculateRoute(positions, profile, 'gpx', undefined, sessionFunctions);
    if (!gpxData || typeof gpxData !== 'string') {
      console.error('No GPX data received or data is not a string:', typeof gpxData);
      return;
    }

    // Erstelle einen Blob aus den GPX-Daten
    const blob = new Blob([gpxData as string], { type: 'application/gpx+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Use route name if provided, otherwise use date
    const fileName = routeName 
      ? `${routeName.replace(/[^a-zA-Z0-9-_]/g, '_')}.gpx`
      : `cycling-route-${new Date().toISOString().split('T')[0]}.gpx`;
    
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Fehler beim Herunterladen der GPX-Route:', error);
    throw error;
  } finally {
    // Notify that loading has ended
    if (onLoadingEnd) {
      onLoadingEnd();
    }
  }
};

// Function to combine consecutive segments with the same surface type
export const combineConsecutiveSegments = (segments: any[]): any[] => {
  if (!segments || segments.length === 0) {
    return [];
  }
  
  const combinedSegments = [];
  let currentSegment = null;
  
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    
    // Skip segments without surface data or distance
    if (!segment.surface || !segment.Distance || segment.Distance <= 0) {
      // If we have a current segment, push it and reset
      if (currentSegment) {
        combinedSegments.push(currentSegment);
        currentSegment = null;
      }
      // Add the segment without surface data as-is
      if (segment.Distance && segment.Distance > 0) {
        combinedSegments.push(segment);
      }
      continue;
    }
    
    // If this is the first segment with surface data, or surface type changed
    if (!currentSegment || currentSegment.surface !== segment.surface) {
      // If we have a current segment, push it first
      if (currentSegment) {
        combinedSegments.push(currentSegment);
      }
      
      // Start a new combined segment
      currentSegment = {
        ...segment,
        Distance: Number(segment.Distance),
        combinedSegmentCount: 1, // Track how many original segments were combined
        originalSegments: [segment] // Keep reference to original segments
      };
    } else {
      // Same surface type - combine with current segment
      currentSegment.Distance += Number(segment.Distance);
      currentSegment.combinedSegmentCount++;
      currentSegment.originalSegments.push(segment);
      
      // Update other numeric properties by taking weighted averages or sums as appropriate
      if (segment.Elevation !== null && segment.Elevation !== undefined) {
        // Take the average elevation (could be improved with distance-weighted average)
        const totalElevation = (currentSegment.Elevation || 0) * (currentSegment.combinedSegmentCount - 1) + Number(segment.Elevation);
        currentSegment.Elevation = totalElevation / currentSegment.combinedSegmentCount;
      }
    }
  }
  
  // Don't forget to add the last segment if it exists
  if (currentSegment) {
    combinedSegments.push(currentSegment);
  }
  
  return combinedSegments;
};

// Enhanced function to combine segments with additional logic for surface quality
export const combineSegmentsAdvanced = (segments: any[]): any[] => {
  if (!segments || segments.length === 0) {
    return [];
  }
  
  const combinedSegments = [];
  let currentSegment = null;
  
  // Helper function to determine if two segments should be combined
  const shouldCombineSegments = (seg1: any, seg2: any): boolean => {
    // Must have same surface type
    if (seg1.surface !== seg2.surface) {
      return false;
    }
    
    // Must have same surface category
    if (seg1.surfaceCategory !== seg2.surfaceCategory) {
      return false;
    }
    
    // For very short segments (< 50m), always combine if surface matches
    if (Number(seg1.Distance) < 50 || Number(seg2.Distance) < 50) {
      return true;
    }
    
    // For longer segments, consider combining if they're in the same category
    // and have similar characteristics
    return true;
  };
  
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    
    // Skip segments without surface data or distance
    if (!segment.surface || !segment.Distance || segment.Distance <= 0) {
      // If we have a current segment, push it and reset
      if (currentSegment) {
        combinedSegments.push(currentSegment);
        currentSegment = null;
      }
      // Add the segment without surface data as-is
      if (segment.Distance && segment.Distance > 0) {
        combinedSegments.push(segment);
      }
      continue;
    }
    
    // If this is the first segment with surface data, or segments shouldn't be combined
    if (!currentSegment || !shouldCombineSegments(currentSegment, segment)) {
      // If we have a current segment, push it first
      if (currentSegment) {
        combinedSegments.push(currentSegment);
      }
      
      // Start a new combined segment
      currentSegment = {
        ...segment,
        Distance: Number(segment.Distance),
        combinedSegmentCount: 1,
        originalSegments: [segment],
        shortestOriginalSegment: Number(segment.Distance),
        longestOriginalSegment: Number(segment.Distance)
      };
    } else {
      // Combine with current segment
      const segmentDistance = Number(segment.Distance);
      currentSegment.Distance += segmentDistance;
      currentSegment.combinedSegmentCount++;
      currentSegment.originalSegments.push(segment);
      currentSegment.shortestOriginalSegment = Math.min(currentSegment.shortestOriginalSegment, segmentDistance);
      currentSegment.longestOriginalSegment = Math.max(currentSegment.longestOriginalSegment, segmentDistance);
      
      // Update other numeric properties by taking weighted averages
      if (segment.Elevation !== null && segment.Elevation !== undefined) {
        const totalElevation = (currentSegment.Elevation || 0) * (currentSegment.combinedSegmentCount - 1) + Number(segment.Elevation);
        currentSegment.Elevation = totalElevation / currentSegment.combinedSegmentCount;
      }
    }
  }
  
  // Don't forget to add the last segment if it exists
  if (currentSegment) {
    combinedSegments.push(currentSegment);
  }
  
  return combinedSegments;
};

/**
 * Clear the route cache to force fresh calculations
 * Useful when loading saved routes to prevent old cached data from persisting
 */
export const clearRouteCache = (): void => {
  routeCache.clear();
};
